import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateHabit, getGetUserHabitsQueryKey } from "../../generated/hook";
import { CreateHabitInputClassFrequency } from "../../generated/model";
import useAuth from "../../hooks/useAuth";

const CreateHabitPage: React.FC = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] =
    useState<CreateHabitInputClassFrequency>("daily");
  const [target, setTarget] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createHabitMutation = useCreateHabit();

  if (!user) {
    navigate("/signin");
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const habitData = {
      name,
      description,
      frequency,
      target,
    };
    createHabitMutation.mutate(
      { data: habitData },
      {
        onSuccess: () => {
          // Invalidate habits query to refresh the list with the new habit
          queryClient.invalidateQueries({
            queryKey: getGetUserHabitsQueryKey(),
          });
          // Navigate to habits page
          navigate("/habits");
        },
        onError: (error) => {
          // Handle error, e.g., show an error message
          console.error("Failed to create habit:", error);
        },
      }
    );
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create New Habit
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Habit Name"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            label="Frequency"
            fullWidth
            margin="normal"
            value={frequency}
            onChange={(e) =>
              setFrequency(e.target.value as CreateHabitInputClassFrequency)
            }
            required
            SelectProps={{ native: true }}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </TextField>
          <TextField
            label="Target (times per period)"
            type="number"
            fullWidth
            margin="normal"
            value={target}
            onChange={(e) => setTarget(Number(e.target.value))}
            inputProps={{ min: 1 }}
            required
          />
          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={createHabitMutation.isPending}
            >
              {createHabitMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                "Create Habit"
              )}
            </Button>
          </Box>
          {createHabitMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createHabitMutation.error?.message ||
                "Failed to create habit. Please try again."}
            </Alert>
          )}
          {createHabitMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Habit created successfully!
            </Alert>
          )}
        </form>
      </Box>
    </Container>
  );
};

export default CreateHabitPage;
